import React from "react";
import Button from "react-bootstrap/Button";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import BaseHeader from "@/components/Common/BaseHeader";
import Introductions from "@/components/HeaderButtons/Introductions";
import { SIGNUP, POST_WANT_AD } from "@/constants/onBoarding";
import { pageURLs } from "@/constants";
import { HeaderSidebarBaseWrapper } from "./HeaderSidebar";
import BlsSidebar from "@/components/BlsSidebar";
import MobileAppNav from "@/components/MobileTabBar/MobileAppNav";
import { agentTabs } from "@/components/MobileTabBar/navItems";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { useUserDetails } from "@/context";
import { useCopilotEnabled } from "@/hooks/useCopilotEnabled";
import { useAppConfig } from "@/appConfig";
import { getAppPathname } from "@/helpers/basePath";
import { RESULTS_PATH } from "@/helpers/resultsCopy";


export const Sidebar = (props) => <BlsSidebar {...props} />;

const HeaderSidebarAgent = React.forwardRef((allProps, headerRef) => {

  const {
    user,
    userDetails,
    userPlan,
    showAgentMenuNotificationIcon,
    currentURL,
    handleAgentOnboardingModal,
    PremiumPlanModalShow,
    handleLogOut,
    headerHeight,
    handleGoFeed,
    handleGoInventory,
    goTo,
    ContactModalShow,
    BasicPlanModalShow,
    shouldDisplayWantListingButton,
    introductionList,
    isLoadIntroList,
    unreadMessageCount,
    unreadChatCount,
    dmMessageCount,
    introCountKnown,
    chatCountKnown,
    dmCountKnown,
    handleHandShakeClick,
    updateIntroStateonHover,
    updateIntroPageOnScroll,
    handleCloseIntroList,
    isGitchaDefaultWebsite,
  } = allProps;

  const navigate = useNavigate();

  const handlePostWantAdClick = () => {
    const hasUser = _.get(user, "userInfo", null);

    if (!hasUser) {
      return handleAgentOnboardingModal({
        showPopup: true,
        userAction: SIGNUP,
        userPreference: POST_WANT_AD,
      });
    }

    return navigate(pageURLs.agentCreatewantadMainScreen);
  };

  const handleGoClients = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentMyClients) {
      goTo(pageURLs.agentMyClients);
    }
  };

  const handleGoResults = () => {
    if (!_.get(user, "userInfo", null)) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== RESULTS_PATH) {
      goTo(RESULTS_PATH);
    }
  };

  const handleGoWantListings = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentMyWantAdListings) {
      goTo(pageURLs.agentMyWantAdListings);
    }
  };

  const { shouldToggleColor } = useUserDetails();

  const copilotEnabled = useCopilotEnabled();
  const { flags } = useAppConfig() || {};
  const analyticsEnabled = Boolean(flags?.analytics);
  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  const handleGoChat = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentChat) {
      goTo(pageURLs.agentChat);
    }
  };

  const headerContent = HeaderSidebarBaseWrapper({
    ...allProps,
    headerExtraButtons: () => {

      if (!shouldDisplayWantListingButton) {
        return null;
      }

      return (
        <Button
          variant={shouldToggleColor ? "tertiary" : "primary"}
          className="ml-6 mr-2 btn-tweak d-none d-lg-inline-flex"
          onClick={handlePostWantAdClick}
        >
          Post Want-Listing
        </Button>
      )
    },
    sidebar: <Sidebar
      currentURL={currentURL}
      handleGoInventory={handleGoInventory}
      handleGoFeed={handleGoFeed}
      handleGoClients={handleGoClients}
      handleGoWantListings={handleGoWantListings}
      handleGoChat={handleGoChat}
      handleGoResults={analyticsEnabled ? handleGoResults : undefined}
      shouldToggleColor={shouldToggleColor}
    />,
    mobileNav: _.get(user, "userInfo", null) ? (
      <MobileAppNav
        tone={isGitchaDefaultWebsite ? "gitcha" : "tenant"}
        items={agentTabs({
          signedIn: true,
          canPost: !shouldRestrictAgent,
          copilotEnabled,
          analyticsEnabled,
          isGitchaDefaultWebsite,
          actions: {
            feed: handleGoFeed,
            wantListings: handleGoWantListings,
            post: handlePostWantAdClick,
            genie: handleGoChat,
            agents: () => navigate(pageURLs.agentDirectory),
            analytics: handleGoResults,
          },
          active: {
            feed: currentURL === "/" || currentURL === pageURLs.home,
            wantListings: currentURL === pageURLs.agentMyWantAdListings,
            post: currentURL === pageURLs.agentCreatewantadMainScreen,
            genie: currentURL.startsWith(pageURLs.agentChat),
            agents: currentURL.startsWith(pageURLs.agentDirectory),
            analytics: currentURL === RESULTS_PATH,
          },
        })}
        inbox={{
          unreadIntroductions: unreadMessageCount || 0,
          unreadMessages: shouldRestrictAgent ? 0 : (unreadChatCount || 0) + (dmMessageCount || 0),
          countsKnown: Boolean(introCountKnown && (shouldRestrictAgent || (chatCountKnown && dmCountKnown))),
          showMessages: !shouldRestrictAgent,
          onOpen: handleHandShakeClick,
          onOpenMessages: () => navigate("/messages"),
          introductions: (
            <Introductions
              list={introductionList}
              updateIntroStateonHover={updateIntroStateonHover}
              updateIntroPageOnScroll={updateIntroPageOnScroll}
              isLoadIntroList={isLoadIntroList}
              handleCloseIntroList={handleCloseIntroList}
            />
          ),
        }}
      />
    ) : null,
    authButtonsProps: {
      signInText: "Login",
      handleAgentOnboardingModal: handleAgentOnboardingModal,
    },
    headerSidebarDropdownMenuProps: {
      user,
      userDetails,
      userPlan,
      showAgentMenuNotificationIcon,
      navigate,
      PremiumPlanModalShow,
      handleLogOut,
      ContactModalShow,
      BasicPlanModalShow
    },
  })

  return <BaseHeader {...allProps} headerContent={headerContent} />;

})

export default HeaderSidebarAgent;
